/**
 * US phone input formatting.
 *
 * Applications only accept US numbers, so every phone field formats as the
 * applicant types: (555) 123-4567. That is the same shape ContactPhone renders
 * stored numbers in (see ContactPhone::getPhoneAttribute). Digits are capped at
 * 10; everything the user types or pastes is reduced to digits first, so dashes,
 * spaces, parentheses and a leading +1 are all accepted input.
 *
 * Usage: add `js-us-phone` to the input. Fields present at DOMContentLoaded are
 * bound automatically; call USPhone.bind(el) for inputs added later, and
 * USPhone.unbind(el) where a field can switch to international entry.
 */
(function (window, document) {
    'use strict';

    var MAX_DIGITS = 10;

    /**
     * Reduce to digits, drop a US country-code prefix, cap at 10.
     */
    function digitsOf(value) {
        var digits = String(value == null ? '' : value).replace(/\D/g, '');

        if (digits.length > MAX_DIGITS && digits.charAt(0) === '1') {
            digits = digits.slice(1);
        }

        return digits.slice(0, MAX_DIGITS);
    }

    /**
     * 5551234567 -> (555) 123-4567, formatting partial input as it grows.
     */
    function format(value) {
        var digits = digitsOf(value);

        if (!digits.length) {
            return '';
        }

        if (digits.length <= 3) {
            return '(' + digits;
        }

        if (digits.length <= 6) {
            return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
        }

        return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
    }

    /**
     * Number of digits sitting to the left of the caret, so the caret can be
     * put back in the same logical spot after the value is re-formatted.
     */
    function digitsBeforeCaret(value, caret) {
        return String(value).slice(0, caret).replace(/\D/g, '').length;
    }

    /**
     * Offset in the formatted string that sits just after `count` digits.
     */
    function caretForDigit(formatted, count) {
        var seen = 0;
        var i;

        if (count === 0) {
            return 0;
        }

        for (i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted.charAt(i))) {
                seen++;
                if (seen === count) {
                    return i + 1;
                }
            }
        }

        return formatted.length;
    }

    function reformat(input) {
        var previous = input.value;
        var caret = null;
        var formatted = format(previous);

        try {
            caret = input.selectionStart;
        } catch (e) {
            // Some input types do not expose a selection; formatting still applies.
            caret = null;
        }

        if (formatted === previous) {
            return;
        }

        input.value = formatted;

        if (caret !== null) {
            var position = caretForDigit(formatted, digitsBeforeCaret(previous, caret));
            try {
                input.setSelectionRange(position, position);
            } catch (e) {
                // Ignore: caret restore is a nicety, not a requirement.
            }
        }
    }

    function bind(input) {
        if (!input || input.dataset.usPhoneBound === '1') {
            return;
        }

        input.dataset.usPhoneBound = '1';
        // 14 = (555) 123-4567. Keeps a paste of a longer string from sitting in
        // the field before the input handler trims it.
        input.setAttribute('maxlength', '14');

        if (!input.getAttribute('inputmode')) {
            input.setAttribute('inputmode', 'tel');
        }

        var handler = function () {
            reformat(input);
        };

        input.usPhoneHandler = handler;
        input.addEventListener('input', handler);
        input.addEventListener('blur', handler);

        // Server-rendered / autofilled values arrive unformatted. Only a value
        // that is already a US number is reshaped; anything else is left alone
        // so a stored international number is not silently truncated.
        if (input.value) {
            var raw = String(input.value).replace(/\D/g, '');
            var normalized = (raw.length === 11 && raw.charAt(0) === '1') ? raw.slice(1) : raw;

            if (normalized.length === MAX_DIGITS) {
                input.value = format(input.value);
            }
        }
    }

    /**
     * Stop formatting a field, for example when a form switches to
     * international entry. The current value is left exactly as it is.
     */
    function unbind(input) {
        if (!input || input.dataset.usPhoneBound !== '1') {
            return;
        }

        if (input.usPhoneHandler) {
            input.removeEventListener('input', input.usPhoneHandler);
            input.removeEventListener('blur', input.usPhoneHandler);
            input.usPhoneHandler = null;
        }

        delete input.dataset.usPhoneBound;
    }

    function bindAll(root) {
        var scope = root || document;
        Array.prototype.forEach.call(scope.querySelectorAll('.js-us-phone'), bind);
    }

    window.USPhone = {
        bind: bind,
        unbind: unbind,
        bindAll: bindAll,
        format: format,
        digits: digitsOf
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            bindAll();
        });
    } else {
        bindAll();
    }
})(window, document);
